/**
 * BomTab — BOM-BTA1 (Bill of Materials / Outturn Costing).
 *
 * Rebuild theo `plan/production/BOM-BTA1_BUILD_SPEC.md` (v2) + prototype
 * `plan/production/BomBta1.jsx`. Số liệu verify §7:
 *   Total Value Out = 3561.425 · Handling = 49,008.02 VND/ton ·
 *   Labor/Mt = 1.87864 · Power kWh = 4941 · Power/Mt = 0.97913 ·
 *   Var. proc. cost/Mt = 2.85777 (= J64).
 *
 * Công thức (§1 — invariant):
 *   Value = LIFFE + G2 + Premium (NULL ở loss row) · ValueIn = Input×Value ·
 *   ValueOut = Outturn×Value · Active = (in==0 && out==0)?N:Y ·
 *   Extracted/Mt = TotalValueOut − TotalValueIn − VarProcCost/TotalOutturn (÷0 guard).
 *   Labor/Mt = Σ handling steps (VND) / FX · Power/Mt = (kWh×unitPrice/TotalInputTons)/FX
 *   kWh = (meterNew−meterOld) − (subNew−subOld) − extraDeduct.
 *
 * PO chọn ở header BatchReportPage (prop `bomCode`); KHÔNG tự thêm PO picker.
 * KHÔNG còn dummy data — TẤT CẢ từ API: Row set = master Quality `list_and_valo`
 * (bomOptions.qualityItems, is_status=0) + loss rows; Value Input/Output mỗi quality
 * hydrate từ recipe PO (bomDetail.inputs/outputs). Premium/Value/LIFFE/G2/FX = qrefs
 * (v_changecost_quality theo ngày PO); đơn giá = cost master; total_input_tons + bag
 * mix = Issue; Edit persist qua bom_edit/cost_edit; công tơ điện + V/C nạp từ
 * cost_edit. API lỗi (5xx/network) → Alert (KHÔNG fallback dummy). Số rỗng/0 khi
 * chưa có data / chưa chọn PO.
 * Crop-year baseline (§1b): code trong recipe = `inBaseline=true`;
 * diff (added/modified/removed) tính live so với baseline.
 */
import CloseIcon from '@mui/icons-material/Close'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import { Fragment, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'

import {
  useGetBagMixQuery,
  useGetBatchReportPersistQuery,
  useGetBomCropOptionsQuery,
  useGetBomQualityRefsQuery,
  useGetFactoryBomDetailQuery,
  useGetFactoryBomOptionsQuery,
  useSaveBatchReportMutation,
  type BomEditDelta,
  type CostEditDelta,
} from '../../../../store/api/factoryBomApi'
import type { CostListRow } from '../../../../store/api/factoryCostApi'
import { useGetFactoryCostListQuery } from '../../../../store/api/factoryCostApi'

import { COLORS, neutralChipSx, sectionCardSx, sectionHeaderSx, sectionTitleSx } from './styleTokens'
import { fmtN, fmtPct, fmtUsd } from './reportFormat'
import { BlockTitle, ExField, ExLabel, ExVal, NumText, QualityChip, RefNum } from './reportUi'

type Category = 'FAQ' | 'Graded' | 'By-products' | 'Rejects' | 'Others' | 'Loss'

const CATEGORIES: Array<'All' | Category> = ['All', 'FAQ', 'Graded', 'By-products', 'Rejects', 'Others', 'Loss']

const CAT_COLOR: Record<Category, string> = {
  FAQ: '#b08968',
  Graded: COLORS.success,
  'By-products': '#bc6c25',
  Rejects: COLORS.danger,
  Others: '#8e9aaf',
  Loss: '#9a8c98',
}

const DIFF_COLOR = { added: '#e0902b', modified: '#3a7bd5', removed: COLORS.textSubtle } as const

/* ---------- helpers ----------------------------------------------------- */

const toNum = (s: string): number => {
  if (!s) return 0
  const n = Number(s)
  return Number.isFinite(n) ? n : 0
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
/** ISO `YYYY-MM-DD` → `DD-MMM-YYYY` (vd 2026-03-13 → 13-Mar-2026). */
const fmtDate = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  return `${m[3]}-${MONTHS[Number(m[2]) - 1] ?? m[2]}-${m[1]}`
}

/** Reject ký tự không phải số (digits + 1 dấu thập phân) — §5.7. */
const numericGuard = (raw: string): boolean => raw === '' || /^\d*\.?\d*$/.test(raw)

/** Lỗi API "thật" (server/network) → cần alert. KHÔNG tính 404 (không có data của PO
 *  đó là trạng thái rỗng hợp lệ, không phải lỗi). */
const isRealApiError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false
  const s = (err as { status?: unknown }).status
  if (s === 'FETCH_ERROR' || s === 'TIMEOUT_ERROR' || s === 'PARSING_ERROR') return true
  return typeof s === 'number' && s >= 500
}

type DiffStatus = 'unchanged' | 'modified' | 'added' | 'removed'

interface DraftRow {
  code: string
  cat: Category
  prem: number | null
  /** % In/Out (Edit) — giá trị user nhập vào (fraction, '' = null). */
  editInput: string
  editOut: string
  /** % In/Out (Default) — giá trị từ BomCode (fraction, null = không có trong BOM). */
  defInput: number | null
  defOut: number | null
  inBaseline: boolean
}

/* ---------- main tab ---------------------------------------------------- */

export interface BomTabProps {
  /** Bom code load từ PO List row khớp PO No đang chọn ở header. */
  bomCode?: string
  /** Factory code (vd 'baoloc') — dùng gọi BOM API `/factories/{code}/bom`. */
  factoryCode?: string
  /** Ngày PO (đã format DD-MMM-YYYY) của PO đang chọn → hiển thị ở "Last revised". */
  poDate?: string
  /** Ngày PO ISO (YYYY-MM-DD) → filter LIFFE/DIFF từ v_changecost_quality. */
  poDateISO?: string
  /** PO No đang chọn → load/save batch-report persist (bom_edit/cost_edit). */
  poNo?: string
  /** Permission gating cho nút Save (4-layer [[feedback-permission-rule]]). */
  canEdit?: boolean
  /** Nút Advanced ở header BatchReportPage — bật thì hiện cột % In/Out (Edit) + (Default). */
  showAdvanced?: boolean
  /** Báo parent có thay đổi chưa lưu (để confirm khi đổi PO / rời trang). */
  onDirtyChange?: (dirty: boolean) => void
}

/** Imperative handle cho parent: lưu toàn bộ delta (Detail + Cost). */
export interface BomTabHandle {
  /** Lưu cả bom_edit + cost_edit. Trả true nếu thành công (hoặc không cần lưu). */
  saveAll: () => Promise<boolean>
}

const KNOWN_CATS = new Set<Category>(CATEGORIES.filter((c): c is Category => c !== 'All'))
/** Map category string từ `list_and_valo` → union Category (fallback 'Others'). */
const asCategory = (s: unknown): Category =>
  typeof s === 'string' && KNOWN_CATS.has(s as Category) ? (s as Category) : 'Others'

const BomTab = forwardRef<BomTabHandle, BomTabProps>(function BomTab(
  { bomCode, factoryCode, poDate, poDateISO, poNo, canEdit = false, showAdvanced = false, onDirtyChange },
  ref,
) {
  // Seed rỗng — LIFFE/G2 nạp từ qrefs (Robusta R13-ST1) theo ngày PO. Không hardcode.
  const [liffe, setLiffe] = useState('')
  const [g2, setG2] = useState('')
  const [filter, setFilter] = useState<'All' | Category>('All')
  const [activeOnly, setActiveOnly] = useState(true) // §17 — default Active=Y only
  // Advanced (nút ở header BatchReportPage): OFF → chỉ show % In/Out (Finalize);
  // ON → thêm cột Edit + Default. `advanced` thực tế còn phụ thuộc recipe của PO
  // (xem `recipeDefined` bên dưới): PO đã định nghĩa quality_input + quality_output
  // → Detail luôn collapse như tắt Advanced. Định nghĩa sau khi có bomDetail.
  // Rows rỗng tới khi master list_and_valo (qualityMaster) load → dựng từ API.
  const [rows, setRows] = useState<DraftRow[]>([])
  const [costOpen, setCostOpen] = useState(false)
  // Dirty flags — chỉ enable nút Save khi có Edit; reset sau hydrate (load PO /
  // sau Save). Khai báo sớm vì effect hydrate bên dưới reset chúng.
  const [detailDirty, setDetailDirty] = useState(false)
  const [costDirty, setCostDirty] = useState(false)

  // Cost master (bom-cost) — load khi có factoryCode. Cấp đơn giá cho Handling
  // (link 3 bước lookup) + Power (đơn giá điện) + popup "View unit costs".
  const { data: costList, isFetching: costFetching, error: costErr } = useGetFactoryCostListQuery(
    { factoryCode: factoryCode ?? '' },
    { skip: !factoryCode, refetchOnMountOrArgChange: true },
  )

  // cost_item_code → unit_price (chỉ item có giá), dùng link Handling/Power +
  // đơn giá điện (ELECTRICITY). Kiểm HIỆU LỰC (valid_from/valid_to) so với ngày PO:
  // row in-effect = valid_from <= poDate <= valid_to (null = mở 1 đầu). VẪN GIỮ row
  // ngoài hạn (để vừa có giá tính, vừa cờ `inEffect=false` cho UI tô đậm + alert).
  // Chọn row: ưu tiên in-effect; nếu chỉ có row ngoài hạn → lấy valid_from mới nhất.
  // Chưa chọn PO (không có poDate) → coi như mọi row đều in-effect.
  const costByCode = useMemo(() => {
    const asOf = poDateISO || ''
    const day = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '')
    const inEffect = (it: CostListRow): boolean => {
      if (!asOf) return true
      const from = day(it.valid_from)
      const to = day(it.valid_to)
      if (from && asOf < from) return false
      if (to && asOf > to) return false
      return true
    }
    const m = new Map<string, { price: number; inEffect: boolean; validFrom: string; validTo: string }>()
    // Sort valid_from tăng dần; set sau ghi đè. Không để row ngoài hạn ghi đè row
    // in-effect đã chọn → in-effect luôn thắng row tương lai/hết hạn.
    ;[...(costList?.items ?? [])]
      .filter((it) => it.unit_price != null)
      .sort((a, b) => day(a.valid_from).localeCompare(day(b.valid_from)))
      .forEach((it) => {
        const eff = inEffect(it)
        const prev = m.get(it.cost_item_code)
        if (prev?.inEffect && !eff) return
        m.set(it.cost_item_code, {
          price: Number(it.unit_price),
          inEffect: eff,
          validFrom: day(it.valid_from),
          validTo: day(it.valid_to),
        })
      })
    return m
  }, [costList, poDateISO])

  // Giá-only map (giữ chữ ký cũ cho khối tính `cost`).
  // QUÁ HẠN → đơn giá = 0: cost ngoài [valid_from, valid_to] so với ngày PO
  // KHÔNG được dùng giá cũ (tránh tính sai). UI sẽ Alert đỏ-nền-vàng cảnh báo.
  const unitByCode = useMemo(() => {
    const m = new Map<string, number>()
    costByCode.forEach((v, k) => m.set(k, v.inEffect ? v.price : 0))
    return m
  }, [costByCode])

  // 1 API CHUNG: quality-refs (v_changeprice_batch WHERE date_actual = PO date).
  // Dùng cho cả Header (Liffe/Diff: code R13-ST1 + A13-WA1) lẫn Detail
  // (Premium/Value mỗi quality). Liffe=last, Diff=diff, value=liffe+diff+premium.
  const { data: qrefs, error: qrefsErr } = useGetBomQualityRefsQuery(
    { factoryCode: factoryCode ?? '', date: poDateISO || undefined },
    { skip: !factoryCode, refetchOnMountOrArgChange: true },
  )
  const refByCode = useMemo(() => {
    const m = new Map<string, { liffe: number | null; diff: number | null; premium: number | null; value: number | null }>()
    qrefs?.items.forEach((it) => m.set(it.code, { liffe: it.liffe, diff: it.diff, premium: it.premium, value: it.value }))
    return m
  }, [qrefs])
  const qrefRobusta = refByCode.get('R13-ST1')
  const qrefArabica = refByCode.get('A13-WA1')

  /* --- BOM Input/Output load từ API theo crop-year hiện tại (§5.10) ---------
   * Options → currentCropYear; Detail({factoryCode, bomCode, cropYear}) →
   * inputs[]/outputs[]. Hydrate input/out values lên master list_and_valo; code chỉ
   * có trong recipe/edit mà không có trong master → append, đánh dấu added. */
  const { data: bomOptions, error: optsErr } = useGetFactoryBomOptionsQuery(factoryCode ?? '', {
    skip: !factoryCode,
    refetchOnMountOrArgChange: true,
  })

  // Persist (DELTA) đã lưu cho PO này — hydrate lớp Edit lên trên Default + neo
  // crop. RTK dedupe: BatchReportPage cũng query cùng key (dùng cho landing tab).
  const { data: persist, error: persistErr } = useGetBatchReportPersistQuery(
    { factoryCode: factoryCode ?? '', poNo: poNo ?? '' },
    { skip: !factoryCode || !poNo, refetchOnMountOrArgChange: true },
  )
  const bomEdit = persist?.bomEdit ?? null
  const costEdit = persist?.costEdit ?? null
  const [saveBatchReport, { isLoading: saving }] = useSaveBatchReportMutation()

  // BOM Crop dùng cho report: ưu tiên crop đã lưu (`bom_crop` PO form / lần Save
  // trước); chưa có thì crop chứa PO date (crop-options `suggested`) nếu có recipe,
  // ngược lại crop mới nhất có recipe; fallback currentCropYear.
  const { data: cropOpts, error: cropErr } = useGetBomCropOptionsQuery(
    { factoryCode: factoryCode ?? '', bomCode: bomCode ?? '', date: poDateISO || undefined },
    { skip: !factoryCode || !bomCode, refetchOnMountOrArgChange: true },
  )
  const cropYear = useMemo(() => {
    const saved = persist?.bomCrop?.trim()
    if (saved) return saved
    const years = cropOpts?.cropYears ?? []
    const sug = cropOpts?.suggested ?? ''
    if (sug && years.includes(sug)) return sug
    if (years.length) return years[0]
    return sug || bomOptions?.currentCropYear || ''
  }, [persist, cropOpts, bomOptions])
  const { data: bomDetail, error: detailErr, isFetching: detailFetching } = useGetFactoryBomDetailQuery(
    { factoryCode: factoryCode ?? '', bomCode: bomCode ?? '', cropYear },
    { skip: !factoryCode || !bomCode || !cropYear, refetchOnMountOrArgChange: true },
  )

  // PO này đã định nghĩa quality_input VÀ quality_output trong recipe chưa?
  // Có → Block Detail hiển thị như tắt Advanced (ẩn Edit/Default), KHÔNG cho edit.
  // Rỗng cả 2 → cho mở Advanced + edit.
  const recipeDefined = useMemo(() => {
    const hasIn = (bomDetail?.inputs ?? []).some((c) => c.quality_value != null && c.quality_value !== 0)
    const hasOut = (bomDetail?.outputs ?? []).some((c) => c.quality_value != null && c.quality_value !== 0)
    return hasIn && hasOut
  }, [bomDetail])
  const advanced = showAdvanced && !recipeDefined

  // Danh sách Quality của bảng BOM = master `list_and_valo` (qualityItems,
  // is_status=0). Sort theo order_by rồi code.
  // Category BOM = cột `phase` (FAQ/Graded/By-products/Rejects/Others).
  const qualityMaster = useMemo(() => {
    const items = bomOptions?.qualityItems ?? []
    return [...items].sort((a, b) => {
      const oa = Number(a.order_by ?? Number.MAX_SAFE_INTEGER)
      const ob = Number(b.order_by ?? Number.MAX_SAFE_INTEGER)
      if (oa !== ob) return oa - ob
      return String(a.code ?? '').localeCompare(String(b.code ?? ''))
    })
  }, [bomOptions])

  const qualityCatByCode = useMemo(() => {
    const m = new Map<string, Category>()
    qualityMaster.forEach((q) => m.set(String(q.code), asCategory(q.phase)))
    return m
  }, [qualityMaster])

  useEffect(() => {
    // Chờ master list_and_valo (qualityMaster). Chưa có → rows rỗng (không dummy).
    if (qualityMaster.length === 0) return

    // Recipe của PO (bomDetail) → Value Input/Output mỗi quality. Load lên ngay khi
    // chọn PO (bomDetail về); chưa có PO → null (ô trống).
    const inMap = new Map<string, number | null>()
    const outMap = new Map<string, number | null>()
    bomDetail?.inputs.forEach((c) => inMap.set(c.quality_code, c.quality_value))
    bomDetail?.outputs.forEach((c) => outMap.set(c.quality_code, c.quality_value))

    // Edit overlay (DELTA đã lưu): bom_edit.items[code].in/out → editInput/editOut.
    const editItems = bomEdit?.items ?? {}
    const editStr = (code: string, k: 'in' | 'out'): string => {
      const v = editItems[code]?.[k]
      return v == null ? '' : String(v)
    }

    const LOSS_CODES = ['Processing loss', 'Moisture loss'] as const
    const masterCodes = new Set(qualityMaster.map((q) => String(q.code)))

    // Row set = TẤT CẢ quality từ master list_and_valo; defInput/defOut từ recipe PO.
    const masterRows: DraftRow[] = qualityMaster.map((q) => {
      const code = String(q.code)
      return {
        code,
        cat: asCategory(q.phase),
        prem: 0, // Premium THỰC từ qrefs ở `computed` (ref?.premium ?? r.prem).
        editInput: editStr(code, 'in'),
        editOut: editStr(code, 'out'),
        defInput: inMap.has(code) ? inMap.get(code) ?? null : null,
        defOut: outMap.has(code) ? outMap.get(code) ?? null : null,
        inBaseline: true,
      }
    })

    // Loss rows (Processing/Moisture) — default từ BOM Code; prem=null.
    const lossRows: DraftRow[] = LOSS_CODES.map((code) => ({
      code,
      cat: 'Loss' as Category,
      prem: null,
      editInput: editStr(code, 'in'),
      editOut: editStr(code, 'out'),
      defInput: null,
      defOut: code === 'Processing loss' ? bomDetail?.processingLoss ?? null : bomDetail?.moistureLoss ?? null,
      inBaseline: true,
    }))

    // Off-master codes (recipe/edit có, master + loss không) → append, added.
    const extraCodes = new Set<string>()
    ;[...inMap.keys(), ...outMap.keys(), ...Object.keys(editItems)].forEach((code) => {
      if (!masterCodes.has(code) && !LOSS_CODES.includes(code as (typeof LOSS_CODES)[number])) extraCodes.add(code)
    })
    const extraRows: DraftRow[] = [...extraCodes].map((code) => ({
      code,
      cat: qualityCatByCode.get(code) ?? 'Others',
      prem: 0,
      editInput: editStr(code, 'in'),
      editOut: editStr(code, 'out'),
      defInput: inMap.has(code) ? inMap.get(code) ?? null : null,
      defOut: outMap.has(code) ? outMap.get(code) ?? null : null,
      inBaseline: false,
    }))

    // Hydrate state cục bộ từ server data (load PO) — pattern hydrate hợp lệ;
    // chỉ chạy khi deps đổi & hội tụ, không tạo vòng lặp.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRows([...masterRows, ...lossRows, ...extraRows])
    setDetailDirty(false) // vừa hydrate từ persist → sạch
  }, [qualityMaster, qualityCatByCode, bomDetail, bomEdit])
  // Cost breakdown — 3 bước lookup (Intake/Hopper/Packing) link từ cost master;
  // 2 bước V/C manual (chưa có nguồn BE) editable; đơn giá điện link từ master.
  // V/C transport (manual) — rỗng tới khi nạp từ persist (cost_edit.vc_*) của PO đã
  // lưu; PO chưa lưu để trống cho user nhập. KHÔNG seed '0' cố định.
  const [vcHopper, setVcHopper] = useState('') // V/C đến hopper
  const [vcWarehouse, setVcWarehouse] = useState('') // V/C vào kho
  // Bag mix (tons) BB vs PP/JB — CÔNG THỨC từ bảng Issue theo PO (không nhập tay),
  // dùng volume-weight đơn giá Hopper/Packing (§4a).
  const { data: bagMix, error: bagErr } = useGetBagMixQuery(
    { factoryCode: factoryCode ?? '', poNo: poNo ?? '' },
    { skip: !factoryCode || !poNo, refetchOnMountOrArgChange: true },
  )
  const bbTonsNum = bagMix?.bb_tons ?? 0
  const ppjbTonsNum = bagMix?.ppjb_tons ?? 0
  // Tấn theo hình thức nhập (Pallet/Stack-lên cây/Processing) — CÔNG THỨC từ bảng
  // Issue (sheet Code CF3:CF5), dùng để weighted đơn giá Intake (§4).
  const palletTonsNum = bagMix?.pallet_tons ?? 0
  const stackTonsNum = bagMix?.stack_tons ?? 0
  const procTonsNum = bagMix?.proc_tons ?? 0

  // Lỗi API THẬT (server/network 5xx) → hiện Alert thay vì fallback dummy (đã gỡ hết
  // dummy). 404 (PO chưa có data) là rỗng hợp lệ, không tính lỗi.
  const apiErrors = [
    isRealApiError(optsErr) && 'Quality list',
    isRealApiError(qrefsErr) && 'Prices (LIFFE / Premium / FX)',
    isRealApiError(detailErr) && 'BOM recipe',
    isRealApiError(costErr) && 'Cost master',
    isRealApiError(bagErr) && 'Bag mix (tonnage)',
    isRealApiError(persistErr) && 'Saved edits',
    isRealApiError(cropErr) && 'Crop options',
  ].filter(Boolean) as string[]

  // Số công tơ điện — rỗng tới khi nạp từ persist (cost_edit.power) của PO đã lưu;
  // PO chưa lưu để trống cho user nhập. KHÔNG seed số PO mẫu.
  const [meterNew, setMeterNew] = useState('')
  const [meterOld, setMeterOld] = useState('')
  const [submeterNew, setSubmeterNew] = useState('')
  const [submeterOld, setSubmeterOld] = useState('')
  const [extraDeduct, setExtraDeduct] = useState('')

  // Hydrate scalar inputs từ persist (DELTA): LIFFE/G2 (bom_edit) + V/C transport
  // + bag mix (cost_edit). Re-apply khi persist đổi (load PO / sau Save).
  /* eslint-disable react-hooks/set-state-in-effect -- hydrate scalar inputs từ
     server data (load PO); chỉ chạy khi deps đổi & hội tụ, không vòng lặp. */
  useEffect(() => {
    if (bomEdit?.liffe != null) setLiffe(String(bomEdit.liffe))
    if (bomEdit?.g2_diffs != null) setG2(String(bomEdit.g2_diffs))
    if (costEdit?.vc_hopper != null) setVcHopper(String(costEdit.vc_hopper))
    if (costEdit?.vc_warehouse != null) setVcWarehouse(String(costEdit.vc_warehouse))
    if (costEdit?.power?.meter_new != null) setMeterNew(String(costEdit.power.meter_new))
    if (costEdit?.power?.meter_old != null) setMeterOld(String(costEdit.power.meter_old))
    if (costEdit?.power?.submeter_new != null) setSubmeterNew(String(costEdit.power.submeter_new))
    if (costEdit?.power?.submeter_old != null) setSubmeterOld(String(costEdit.power.submeter_old))
    if (costEdit?.power?.extra_deduct != null) setExtraDeduct(String(costEdit.power.extra_deduct))
    setCostDirty(false) // vừa hydrate từ persist → sạch
  }, [bomEdit, costEdit])
  /* eslint-enable react-hooks/set-state-in-effect */

  const liffeNum = toNum(liffe)
  const g2Num = toNum(g2)
  // FX (VND/USD) live theo ngày PO — cùng nguồn qrefs với Liffe/Diff/Premium.
  // Global per-date nên lấy item đầu có fx; 0 khi chưa có data (guard fx>0 ở cost calc).
  const fx = useMemo(() => {
    const it = qrefs?.items.find((i) => i.fx != null)
    return it?.fx ?? 0
  }, [qrefs])
  // Total input (Mt) — mẫu số Power/Mt & hiển thị Summary. LIVE theo PO = tổng
  // tons issued (BB + PP/JB) từ bảng Issue (view_factory_issue2, cùng nguồn bag
  // mix; SUM(issued_nw)/1000). Không còn hardcode 506.043 của PO mẫu. 0 khi chưa
  // có Issue → costPerTon guard ÷0 ở dưới.
  const tons = bbTonsNum + ppjbTonsNum

  /* --- cost breakdown (§4a/§4b) — link cost master + volume-weight bag mix ---
   * 3 bước lookup (Intake/Hopper/Packing) đơn giá từ cost master; Hopper/Packing
   * weighted theo bag mix (BB vs PP/JB). 2 bước V/C manual. Labor/Mt = Σ / FX.
   * Power: đơn giá điện từ master (ELECTRICITY), meter readings là input hiện có. */
  const cost = useMemo(() => {
    const bb = bbTonsNum
    const pp = ppjbTonsNum
    const mix = bb + pp
    const wavg = (bbCode: string, ppCode: string): number => {
      const b = unitByCode.get(bbCode) ?? 0
      const p = unitByCode.get(ppCode) ?? 0
      return mix > 0 ? (bb * b + pp * p) / mix : b
    }
    // Intake = bình quân gia quyền 3 hình thức nhập theo tấn (sheet Code CE11):
    // Pallet / Stack (lên cây) / Processing. Σ tấn = 0 → 0 (IFERROR Code!CE11).
    const intakeRate = (code: string) => unitByCode.get(code) ?? 0
    const intakeTons = palletTonsNum + stackTonsNum + procTonsNum
    const intake = intakeTons > 0
      ? (palletTonsNum * intakeRate('INBOUND_PALLET')
        + stackTonsNum * intakeRate('INBOUND_STACK')
        + procTonsNum * intakeRate('INBOUND_PRODUCTION')) / intakeTons
      : 0
    const hopper = wavg('HOPPER_BB', 'HOPPER_PP_JB')
    const packing = wavg('PACKING_BB', 'PACKING_PP_JB')
    const vcH = toNum(vcHopper)
    const vcW = toNum(vcWarehouse)
    const handlingTotal = intake + vcH + hopper + packing + vcW
    const laborPerMt = fx > 0 ? handlingTotal / fx : 0

    // Đơn giá điện = item ELECTRICITY (cost master, đã lọc theo hiệu lực ngày PO).
    // Thiếu → 0 (signal data trống, không dùng hằng PO mẫu).
    const electricityUnit = unitByCode.get('ELECTRICITY') ?? 0
    // Guard ≥ 0: sub-meter/extra trừ quá tay (nhập lệch) không cho ra kWh âm →
    // điện/Power âm vô nghĩa. Clamp về 0.
    const kwh = Math.max(0, toNum(meterNew) - toNum(meterOld) - (toNum(submeterNew) - toNum(submeterOld)) - toNum(extraDeduct))
    const electricity = kwh * electricityUnit
    const costPerTon = tons > 0 ? electricity / tons : 0
    const powerPerMt = fx > 0 ? costPerTon / fx : 0
    return {
      intake,
      hopper,
      packing,
      vcH,
      vcW,
      handlingTotal,
      laborPerMt,
      electricityUnit,
      kwh,
      electricity,
      costPerTon,
      powerPerMt,
      varProcPerMt: laborPerMt + powerPerMt,
    }
  }, [unitByCode, bbTonsNum, ppjbTonsNum, palletTonsNum, stackTonsNum, procTonsNum, vcHopper, vcWarehouse, meterNew, meterOld, submeterNew, submeterOld, extraDeduct, fx, tons])

  /* --- Cảnh báo cost ngoài hiệu lực (valid_from/valid_to vs ngày PO) -----------
   * Mỗi đơn giá lấy từ cost master nhưng ngày PO nằm ngoài [valid_from, valid_to]
   * → cờ row (tô đậm + ⚠) + danh sách cho Alert. Weighted (Hopper/Packing) tính
   * theo code thực sự có khối lượng (bag mix); không có mix → dùng BB. */
  const costAlerts = useMemo(() => {
    const meta = (code: string) => costByCode.get(code)
    const expired = (code: string) => { const m = meta(code); return !!m && !m.inEffect }
    const hasMix = bbTonsNum > 0 || ppjbTonsNum > 0
    const weighted = (bbCode: string, ppCode: string) =>
      hasMix
        ? (bbTonsNum > 0 && expired(bbCode)) || (ppjbTonsNum > 0 && expired(ppCode))
        : expired(bbCode)
    const items: Array<{ label: string; from: string; to: string }> = []
    const push = (code: string, label: string) => {
      const m = meta(code)
      if (m && !m.inEffect) items.push({ label, from: m.validFrom, to: m.validTo })
    }
    // Intake: chỉ cảnh báo mã của hình thức nhập THỰC SỰ có tấn (Pallet/Stack/Proc).
    if (palletTonsNum > 0) push('INBOUND_PALLET', 'Intake — to pallet')
    if (stackTonsNum > 0) push('INBOUND_STACK', 'Intake — to stack')
    if (procTonsNum > 0) push('INBOUND_PRODUCTION', 'Intake — processing')
    const intakeExpired =
      (palletTonsNum > 0 && expired('INBOUND_PALLET')) ||
      (stackTonsNum > 0 && expired('INBOUND_STACK')) ||
      (procTonsNum > 0 && expired('INBOUND_PRODUCTION'))
    if (hasMix) {
      if (bbTonsNum > 0) { push('HOPPER_BB', 'Hopper discharge — Big Bag'); push('PACKING_BB', 'Packing — Big Bag') }
      if (ppjbTonsNum > 0) { push('HOPPER_PP_JB', 'Hopper discharge — PP/JB'); push('PACKING_PP_JB', 'Packing — PP/JB') }
    } else {
      push('HOPPER_BB', 'Hopper discharge'); push('PACKING_BB', 'Packing')
    }
    push('ELECTRICITY', 'Electricity unit price')
    return {
      intake: intakeExpired,
      hopper: weighted('HOPPER_BB', 'HOPPER_PP_JB'),
      packing: weighted('PACKING_BB', 'PACKING_PP_JB'),
      electricity: expired('ELECTRICITY'),
      items,
      any: items.length > 0,
    }
  }, [costByCode, bbTonsNum, ppjbTonsNum, palletTonsNum, stackTonsNum, procTonsNum])

  /* --- per-row compute (§1) — Finalize = Edit nếu nhập, ngược lại Default --- */
  const computed = useMemo(
    () =>
      rows.map((r) => {
        const defIn = r.defInput ?? 0
        const defOut = r.defOut ?? 0
        const finInput = r.editInput !== '' ? toNum(r.editInput) : defIn
        const finOut = r.editOut !== '' ? toNum(r.editOut) : defOut
        // Premium + Value lấy từ v_changecost_quality (qrefs) nếu có; loss row
        // (prem=null) giữ null. Fallback: Value = LIFFE + G2 + Premium (template).
        const ref = refByCode.get(r.code)
        const prem = r.prem == null ? null : ref?.premium ?? r.prem
        const value =
          prem == null ? null : ref?.value ?? liffeNum + g2Num + prem
        const valueIn = value == null ? 0 : finInput * value
        const valueOut = value == null ? 0 : finOut * value
        const active: 'Y' | 'N' = finInput === 0 && finOut === 0 ? 'N' : 'Y'
        let diff: DiffStatus = 'unchanged'
        if (!r.inBaseline) diff = 'added'
        else if (finInput === 0 && finOut === 0 && (defIn !== 0 || defOut !== 0)) diff = 'removed'
        else if (finInput !== defIn || finOut !== defOut) diff = 'modified'
        return { ...r, prem, defIn, defOut, finInput, finOut, value, valueIn, valueOut, active, diff }
      }),
    [rows, liffeNum, g2Num, refByCode],
  )

  const totals = useMemo(() => {
    // Excel cộng Value In/Out theo từng dòng ĐÃ làm tròn 2dp (cell ROUND) →
    // web cũng round mỗi dòng trước khi cộng để khớp $ đến cent (tránh lệch FP).
    const round2 = (n: number) => Math.round(n * 100) / 100
    const totalInput = computed.reduce((s, r) => s + r.finInput, 0)
    const totalOutturn = computed.reduce((s, r) => s + r.finOut, 0)
    const totalValueIn = computed.reduce((s, r) => s + round2(r.valueIn), 0)
    const totalValueOut = computed.reduce((s, r) => s + round2(r.valueOut), 0)
    const extractedPerMt =
      totalOutturn > 0 ? totalValueOut - totalValueIn - cost.varProcPerMt / totalOutturn : 0
    return { totalInput, totalOutturn, totalValueIn, totalValueOut, extractedPerMt }
  }, [computed, cost.varProcPerMt])

  // §17 — filtering display-only; totals luôn tính trên TẤT CẢ rows.
  const visible = computed
    .filter((r) => filter === 'All' || r.cat === filter)
    .filter((r) => !activeOnly || r.active === 'Y')

  // Input/Target Quality lấy TRỰC TIẾP từ bảng dữ liệu (chỉ row có % ≠ 0):
  //   Input  = row có input > 0 · Target = row có outturn > 0 (loại loss row).
  const inputQualities = totals.totalInput > 0 ? computed.filter((r) => r.finInput > 0) : []
  const targetQualities =
    totals.totalOutturn > 0 ? computed.filter((r) => r.finOut > 0 && r.prem != null) : []

  // liffe/g2 state (base Value = Liffe + Diff + premium(code)) lấy theo Robusta
  // (R13-ST1) từ v_changeprice_batch. Liffe = last, g2 = Diff (cột diff).
  /* eslint-disable react-hooks/set-state-in-effect -- hydrate Liffe/Diff từ market
     data theo ngày PO; chỉ chạy khi qrefRobusta đổi & hội tụ. */
  useEffect(() => {
    if (!qrefRobusta) return
    if (qrefRobusta.liffe != null) setLiffe(String(qrefRobusta.liffe))
    if (qrefRobusta.diff != null) setG2(String(qrefRobusta.diff))
  }, [qrefRobusta])
  /* eslint-enable react-hooks/set-state-in-effect */

  const setCell = (code: string, key: 'editInput' | 'editOut', raw: string) => {
    if (!editable) return // Finished/no-permission → khoá sửa
    if (!numericGuard(raw)) return
    setRows((prev) => prev.map((r) => (r.code === code ? { ...r, [key]: raw } : r)))
    setDetailDirty(true)
  }
  const setNum = (setter: (v: string) => void) => (raw: string) => {
    if (!editable) return
    if (!numericGuard(raw)) return
    setter(raw)
    setCostDirty(true)
  }

  /* --- Save persistence (DELTA) — bom_edit + cost_edit (§5.10) --------------
   * bom_edit: chỉ items sparse (chỉ code có Edit). cost_edit: V/C transport +
   * bag mix. Default (recipe) + handling lookup KHÔNG lưu (lấy live).
   * Liffe + Diff (g2) KHÔNG lưu — lấy live từ v_changeprice_batch; freeze khi
   * chốt Finished sẽ xử lý sau (chưa giao spec). */
  const buildBomEdit = (): BomEditDelta => {
    const items: NonNullable<BomEditDelta['items']> = {}
    rows.forEach((r) => {
      const e: { in?: number | null; out?: number | null } = {}
      if (r.editInput !== '') e.in = toNum(r.editInput)
      if (r.editOut !== '') e.out = toNum(r.editOut)
      if (Object.keys(e).length) items[r.code] = e
    })
    return { items }
  }
  const buildCostEdit = (): CostEditDelta => ({
    vc_hopper: toNum(vcHopper),
    vc_warehouse: toNum(vcWarehouse),
    // Snapshot bag mix + hình thức nhập (tons) để BE export/PA tính weighted khớp
    // tab live (Hopper/Packing theo BB-PP/JB; Intake theo Pallet/Stack/Processing).
    bb_tons: bbTonsNum,
    ppjb_tons: ppjbTonsNum,
    pallet_tons: palletTonsNum,
    stack_tons: stackTonsNum,
    proc_tons: procTonsNum,
    power: {
      meter_new: toNum(meterNew),
      meter_old: toNum(meterOld),
      submeter_new: toNum(submeterNew),
      submeter_old: toNum(submeterOld),
      extra_deduct: toNum(extraDeduct),
    },
  })

  const poTrim = poNo?.trim() ?? ''
  // Trạng thái: is_finished true = Finished (BR chốt = PO đóng), false = Processing.
  const isFinished = !!persist?.isFinished
  // Finished → KHOÁ sửa (không cho Edit + disable Save). canEdit = permission.
  const editable = canEdit && !isFinished
  // Không cho Edit (no permission / Finished) → ẩn hẳn cột Edited + nút Save Detail.
  const showEditCols = advanced && editable

  // PO được phép Edit (recipe rỗng + có quyền + chưa Finished) → Block Detail mặc
  // định load filter "All", còn lại "Active". CHỜ recipe của ĐÚNG PO load xong
  // (!detailFetching) mới áp — tránh race đọc nhầm recipe PO cũ (lag → ra "All").
  // Reset cờ user mỗi khi đổi PO; chỉ auto-set khi user CHƯA tự đổi filter cho PO
  // hiện tại → recipe settle muộn vẫn tự sửa lại đúng default.
  const userTouchedFilterRef = useRef(false)
  useEffect(() => {
    userTouchedFilterRef.current = false
  }, [poTrim])
  useEffect(() => {
    if (!poTrim || detailFetching) return
    if (userTouchedFilterRef.current) return
    const allowEdit = editable && !recipeDefined
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActiveOnly(!allowEdit)
  }, [poTrim, detailFetching, editable, recipeDefined])
  const canSaveDelta = editable && !!factoryCode && !!poTrim && !saving
  const saveHint = !canEdit
    ? 'No edit permission'
    : isFinished
      ? 'Report is Finished — Mark as Processing to edit'
      : !poTrim
        ? 'Select a PO first'
        : ''

  // Save tách: Detail (bom_edit) / Cost (cost_edit) — partial, không đổi status.
  const handleSaveDetail = () => {
    if (!canSaveDelta) return // 4-layer: button disabled + handler early-return
    void saveBatchReport({
      factoryCode: factoryCode ?? '',
      poNo: poTrim,
      body: { bomCrop: cropYear || null, bomEdit: buildBomEdit() },
    })
  }
  const handleSaveCost = () => {
    if (!canSaveDelta) return
    void saveBatchReport({
      factoryCode: factoryCode ?? '',
      poNo: poTrim,
      body: { costEdit: buildCostEdit() },
    })
  }

  // Báo parent dirty state để confirm khi đổi PO / rời trang.
  const dirty = detailDirty || costDirty
  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])
  // Unmount (đổi tab/rời trang) → reset dirty ở parent để không kẹt stale true.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

  // Imperative: parent gọi saveAll() trong dialog confirm ("Save & switch").
  useImperativeHandle(ref, () => ({
    saveAll: async () => {
      if (!canEdit || !factoryCode || !poTrim || isFinished) return false
      try {
        await saveBatchReport({
          factoryCode,
          poNo: poTrim,
          body: { bomCrop: cropYear || null, bomEdit: buildBomEdit(), costEdit: buildCostEdit() },
        }).unwrap()
        return true
      } catch {
        return false
      }
    },
  }))

  const displayBomCode = bomCode?.trim() || 'BTA1'
  // Last revised + Month-Year = ngày PO đang chọn (poDate đã format DD-MMM-YYYY);
  // '–' khi chưa chọn PO (không còn fallback mock). Period = MMM-YYYY từ poDate.
  const displayLastRevised = poDate?.trim() || '–'
  const displayPeriod = useMemo(() => {
    const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(poDate?.trim() ?? '')
    return m ? `${m[2]}-${m[3]}` : '–'
  }, [poDate])

  return (
    <Stack spacing={2}>
      {/* Lỗi API thật (server/network) → cảnh báo; KHÔNG fallback dummy. */}
      {apiErrors.length > 0 ? (
        <Alert severity="error" sx={{ fontSize: '0.78rem', py: 0.5 }}>
          Could not load from server: {apiErrors.join(', ')}. Please retry or check the connection — values shown may be incomplete.
        </Alert>
      ) : null}

      {/* Trạng thái Finished/Processing + nút Mark as… đã chuyển lên header
          BatchReportPage (cùng dòng PO No.). BomTab chỉ còn Save Detail/Cost. */}

      {/* ===== Header — CSS Grid 3:7: trái = references (Year/Month…), phải = qualities ===== */}
      <Box>
        <BlockTitle>Overview</BlockTitle>
      <Box
        sx={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: 1.5,
          bgcolor: COLORS.bgPage,
          p: 1.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' },
          columnGap: 7,
          rowGap: 1,
          alignItems: 'start',
        }}
      >
        {/* Trái: Month-Year · Last revised · Liffe · Diff · FX (Liffe=last, Diff=diff
            từ v_changeprice_batch, Robusta R13-ST1). Value span cột 2→4. */}
        <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: 'auto auto auto', columnGap: 1.5, rowGap: 0.25, gridAutoRows: 'minmax(22px, auto)', alignItems: 'center', justifyItems: 'end', justifyContent: 'start' }}>
          <Box sx={{ justifySelf: 'start' }}><ExLabel>Month-Year</ExLabel></Box>
          <Box sx={{ gridColumn: '2 / 4' }}><ExVal>{displayPeriod}</ExVal></Box>
          <Box sx={{ justifySelf: 'start' }}><ExLabel>Last revised</ExLabel></Box>
          <Box sx={{ gridColumn: '2 / 4' }}><ExVal>{displayLastRevised}</ExVal></Box>
          <Box sx={{ justifySelf: 'start' }}><ExLabel>Liffe</ExLabel></Box>
          <Box sx={{ gridColumn: '2 / 4' }}><RefNum value={qrefRobusta?.liffe} /></Box>
          <Box sx={{ justifySelf: 'start' }}><ExLabel>Diff</ExLabel></Box>
          <Box sx={{ gridColumn: '2 / 4' }}><RefNum value={qrefRobusta?.diff} /></Box>
          <Box sx={{ justifySelf: 'start' }}><ExLabel>FX</ExLabel></Box>
          <Box sx={{ gridColumn: '2 / 4' }}><ExVal>{`${fmtN(fx)} VND/USD`}</ExVal></Box>
        </Box>
        {/* Phải: qualities (B3:C5) — top-align + cùng rowGap với grid trái để các
            dòng ngang hàng (Year↔Bom, Month↔Input quality, Last revised↔Target).
            Grid `auto 1fr` để value bắt đầu cùng vị trí (label cột 1, value cột 2). */}
        <Box
          sx={{
            minWidth: 0,
            alignSelf: 'start',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: 1.5,
            rowGap: 0.25,
            gridAutoRows: 'minmax(22px, auto)',
            alignItems: 'center',
          }}
        >
          <ExLabel>Bom</ExLabel>
          <Box sx={{ justifySelf: 'start' }}>
            <Chip
              size="small"
              label={displayBomCode}
              sx={{
                height: 22,
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: 0.4,
                color: '#fff',
                bgcolor: COLORS.primary,
                border: `1px solid ${COLORS.primary}`,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </Box>
          <Box sx={{ alignSelf: 'start', mt: '4px' }}><ExLabel>Input quality</ExLabel></Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifySelf: 'start' }}>
            {inputQualities.length === 0 ? (
              <Typography sx={{ fontSize: '0.75rem', color: COLORS.textSubtle }}>—</Typography>
            ) : (
              inputQualities.map((r) => (
                <QualityChip key={r.code} label={r.code} pct={fmtPct(r.finInput * 100, 1)} />
              ))
            )}
          </Box>
          <Box sx={{ alignSelf: 'start', mt: '4px' }}><ExLabel>Target quality</ExLabel></Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifySelf: 'start' }}>
            {targetQualities.length === 0 ? (
              <Typography sx={{ fontSize: '0.75rem', color: COLORS.textSubtle }}>—</Typography>
            ) : (
              targetQualities.map((r) => (
                <QualityChip key={r.code} label={r.code} pct={fmtPct(r.finOut * 100, 1)} />
              ))
            )}
          </Box>
        </Box>
      </Box>
      </Box>

      {/* ===== Totals (Summary) — mirror Excel rows 7-9 (3 cột: B/C · D/E · G/I) ===== */}
      <Box>
        <BlockTitle>Summary</BlockTitle>
      <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1.5, bgcolor: COLORS.bgSubtle, px: 2, py: 1.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, columnGap: 6, rowGap: 0.4 }}>
          <ExField label="Total Input" value={fmtPct(totals.totalInput * 100, 1)} />
          <ExField label="Total Outturn" value={fmtPct(totals.totalOutturn * 100, 1)} />
          <ExField label="Variable processing costs" value={fmtUsd(cost.varProcPerMt)} tone="warn" />
          <ExField label="Total Value In" value={fmtUsd(totals.totalValueIn)} />
          <ExField label="Total Value Out" value={fmtUsd(totals.totalValueOut)} />
          <ExField label="Total upgrading costs" value="–" />
          <ExField label="Total input (Mt)" value={fmtN(tons)} />
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
          <ExField label="Extracted Value per Mt" value={fmtUsd(totals.extractedPerMt)} tone={totals.extractedPerMt >= 0 ? 'success' : 'danger'} strong />
        </Box>
      </Box>
      </Box>

      {/* ===== Outturn Detail — filter + bảng item ===== */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
          <BlockTitle>Detail</BlockTitle>
          {showEditCols && (
            <SaveDeltaButton label="Save Detail" onClick={handleSaveDetail} disabled={!canSaveDelta || !detailDirty} busy={saving} hint={!detailDirty && canSaveDelta ? "No changes to save" : saveHint} />
          )}
        </Stack>
        <Stack spacing={1.25}>
      {/* Filters: Active toggle + Category chips — 1 dòng, gọn (wrap khi quá hẹp). */}
      <Stack direction="row" spacing={0.75} useFlexGap alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={activeOnly ? 'active' : 'all'}
          onChange={(_, v) => {
            if (v) {
              userTouchedFilterRef.current = true // user tự đổi → khoá auto-default
              setActiveOnly(v === 'active')
            }
          }}
          sx={{
            mr: 0.5,
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 1.25,
              py: 0.3,
              fontSize: '0.72rem',
              fontWeight: 600,
              lineHeight: 1.2,
              color: COLORS.textMuted,
              borderColor: COLORS.border,
              '&.Mui-selected': {
                bgcolor: COLORS.primary,
                color: '#fff',
                '&:hover': { bgcolor: COLORS.primary },
              },
            },
          }}
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        {CATEGORIES.map((c) => {
          const on = filter === c
          const dot = c !== 'All' ? CAT_COLOR[c] : undefined
          return (
            <Chip
              key={c}
              size="small"
              clickable
              label={c}
              onClick={() => setFilter(c)}
              icon={dot ? <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot, ml: 0.75 }} /> : undefined}
              sx={{
                height: 24,
                fontSize: '0.72rem',
                fontWeight: on ? 700 : 600,
                bgcolor: on ? COLORS.primary : 'transparent',
                color: on ? '#fff' : COLORS.textMuted,
                border: `1px solid ${on ? COLORS.primary : COLORS.border}`,
                '& .MuiChip-icon': { color: 'inherit' },
                '&:hover': { bgcolor: on ? COLORS.primary : COLORS.bgHover },
              }}
            />
          )
        })}
      </Stack>

      {/* BOM table */}
      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap">
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              // Padding ngang đồng nhất 8px cho MỌI cell (header + body) → mép
              // phải các cột số thẳng hàng; default 16px quá rộng làm số âm rộng
              // (vd ($3,197.00)) tràn cột. Vertical giữ nguyên (compact).
              '& .MuiTableCell-root': { pl: 1, pr: 1 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell className="app-table-header-cell" style={{ width: 34 }} />
                <TableCell className="app-table-header-cell" style={{ width: 138 }}>Item</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 122 }}>Category</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 96 }}>Premium</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 100 }}>Value</TableCell>
                {showEditCols && <TableCell align="right" className="app-table-header-cell" style={{ width: 100 }}><TwoLineHead main="% In" sub="(Edited)" /></TableCell>}
                {advanced && <TableCell align="right" className="app-table-header-cell" style={{ width: 92 }}><TwoLineHead main="% In" sub="(Default)" /></TableCell>}
                <TableCell align="right" className="app-table-header-cell" style={{ width: 96 }}><TwoLineHead main="% In" sub={advanced ? '(Final)' : undefined} /></TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 104 }}>Value In</TableCell>
                {showEditCols && <TableCell align="right" className="app-table-header-cell" style={{ width: 100 }}><TwoLineHead main="% Out" sub="(Edited)" /></TableCell>}
                {advanced && <TableCell align="right" className="app-table-header-cell" style={{ width: 92 }}><TwoLineHead main="% Out" sub="(Default)" /></TableCell>}
                <TableCell align="right" className="app-table-header-cell" style={{ width: 96 }}><TwoLineHead main="% Out" sub={advanced ? '(Final)' : undefined} /></TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 104 }}>Value Out</TableCell>
                <TableCell align="center" className="app-table-header-cell" style={{ width: 66 }}>Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => {
                const isLoss = r.prem == null
                // Edit chỉ mở khi BOM Code (quality) của PO này CHƯA định nghĩa
                // quality_input & quality_output trong recipe (cả 2 rỗng: null/0).
                // Recipe đã có giá trị → khoá Edit (không cho override).
                const recipeEmpty =
                  (r.defInput == null || r.defInput === 0) && (r.defOut == null || r.defOut === 0)
                const rowEditable = editable && recipeEmpty
                return (
                  <TableRow
                    key={r.code}
                    sx={{
                      // Chỉ tô nhẹ dòng đã sửa/off-BOM (hiếm) — KHÔNG tô dòng
                      // active (mặc định filter Y → mọi dòng active, tô hết rối).
                      ...(r.diff === 'modified' ? { bgcolor: 'rgba(58,123,213,0.05)' } : {}),
                      ...(r.diff === 'added' ? { borderLeft: `3px solid ${DIFF_COLOR.added}` } : {}),
                    }}
                  >
                    <TableCell align="center" sx={{ p: 0 }}>
                      <DiffMark diff={r.diff} baseOut={r.defOut} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.code}</TableCell>
                    <TableCell>
                      <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: CAT_COLOR[r.cat], mr: 0.75, verticalAlign: 'middle' }} />
                      {r.cat}
                    </TableCell>
                    <TableCell align="right">{isLoss ? '–' : <NumText n={r.prem} d={2} usd />}</TableCell>
                    <TableCell align="right">{isLoss ? '–' : <NumText n={r.value} d={2} usd />}</TableCell>
                    {showEditCols && (
                      <TableCell align="right" sx={{ p: 0.5 }}>
                        {/* % In (Edit) — chỉ mở khi recipe chưa định nghĩa input+output. */}
                        <PercentCellInput value={r.editInput} onChange={(v) => setCell(r.code, 'editInput', v)} disabled={!rowEditable} />
                      </TableCell>
                    )}
                    {advanced && (
                      <TableCell align="right" sx={{ color: COLORS.textSubtle, fontVariantNumeric: 'tabular-nums' }}>
                        {r.defInput == null || r.defInput === 0 ? '' : fmtPct(r.defInput * 100, 1)}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ color: COLORS.textMuted, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {r.finInput === 0 ? '' : fmtPct(r.finInput * 100, 1)}
                    </TableCell>
                    <TableCell align="right">{r.valueIn === 0 ? '' : <NumText n={r.valueIn} d={2} usd />}</TableCell>
                    {showEditCols && (
                      <TableCell align="right" sx={{ p: 0.5 }}>
                        {/* % Out (Edit) — chỉ mở khi recipe chưa định nghĩa input+output. */}
                        <PercentCellInput value={r.editOut} onChange={(v) => setCell(r.code, 'editOut', v)} disabled={!rowEditable} />
                      </TableCell>
                    )}
                    {advanced && (
                      <TableCell align="right" sx={{ color: COLORS.textSubtle, fontVariantNumeric: 'tabular-nums' }}>
                        {r.defOut == null || r.defOut === 0 ? '' : fmtPct(r.defOut * 100, 1)}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ color: COLORS.textMuted, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {r.finOut === 0 ? '' : fmtPct(r.finOut * 100, 1)}
                    </TableCell>
                    <TableCell align="right">{r.valueOut === 0 ? '' : <NumText n={r.valueOut} d={2} usd />}</TableCell>
                    <TableCell align="center">
                      <ActiveBadge active={r.active} />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
            <TableBody>
              <TableRow>
                <TotalCell colSpan={5}>TOTAL</TotalCell>
                {/* Gate Y HỆT header/body: Edited theo showEditCols, Default theo advanced. */}
                {showEditCols && <TotalCell />}
                {advanced && <TotalCell />}
                <TotalCell align="right">{fmtPct(totals.totalInput * 100, 1)}</TotalCell>
                <TotalCell align="right">{fmtN(totals.totalValueIn)}</TotalCell>
                {showEditCols && <TotalCell />}
                {advanced && <TotalCell />}
                <TotalCell align="right">{fmtPct(totals.totalOutturn * 100, 1)}</TotalCell>
                <TotalCell align="right">{fmtN(totals.totalValueOut)}</TotalCell>
                <TotalCell />
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>
        </Stack>
      </Box>

      {/* ===== Cost Breakdown panels (§4a / §4b / §4.6) ===== */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
          <BlockTitle>Cost Breakdown</BlockTitle>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReceiptLongOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => setCostOpen(true)}
              sx={{
                textTransform: 'none',
                fontSize: '0.7rem',
                fontWeight: 700,
                py: 0.2,
                px: 1,
                minHeight: 0,
                color: COLORS.primary,
                borderColor: COLORS.border,
                '&:hover': { borderColor: COLORS.primary, bgcolor: COLORS.primarySoft },
              }}
            >
              View unit costs
            </Button>
            <SaveDeltaButton label="Save Cost" onClick={handleSaveCost} disabled={!canSaveDelta || !costDirty} busy={saving} hint={!costDirty && canSaveDelta ? "No changes to save" : saveHint} />
          </Stack>
        </Stack>
      {/* Cảnh báo cost ngoài hiệu lực: ngày PO nằm ngoài [valid_from, valid_to]
          của đơn giá lấy từ Cost master → liệt kê + nhắc update window. */}
      {poDateISO && costAlerts.any ? (
        <Alert
          severity="warning"
          sx={{ mb: 1.5, py: 0.25, fontSize: '0.76rem', '& .MuiAlert-message': { py: 0.5 } }}
        >
          <Box component="span" sx={{ fontWeight: 800 }}>
            {costAlerts.items.length} cost rate{costAlerts.items.length > 1 ? 's are' : ' is'} EXPIRED for PO date {displayLastRevised} — forced to 0:
          </Box>
          <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.5 }}>
            {costAlerts.items.map((it) => (
              <Box component="li" key={it.label}>
                <Box component="span" sx={{ fontWeight: 800 }}>{it.label}</Box>
                {' — valid '}
                {it.from ? fmtDate(it.from) : '—'} to {it.to ? fmtDate(it.to) : '—'}
              </Box>
            ))}
          </Box>
          Add a new cost line for this crop: in the Cost master, use Copy to clone the rate with a
          new Valid From / Valid To.
        </Alert>
      ) : null}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="stretch">
        {/* Handling → Labor/Mt */}
        <Box sx={{ ...sectionCardSx, flex: '1 1 0', minWidth: 240 }}>
          <Box sx={{ ...sectionHeaderSx, px: 1.25 }}>
            <Typography sx={{ ...sectionTitleSx, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Handling cost → Labor / Mt</Typography>
            <Chip size="small" label="VND / Mt" sx={neutralChipSx} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            {/* Bag mix (Mt) — CÔNG THỨC từ Issue (read-only): volume-weight Hopper/Packing. */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: COST_LABEL_FS, fontWeight: 700, color: COLORS.textMuted }}>
                Bag mix (Mt)
                <Tooltip title="Computed from the PO's Issue table (bag type BB vs PP/JB)">
                  <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: COLORS.primary }}>ⓘ</Box>
                </Tooltip>
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1.5}>
                <Box component="span" sx={{ fontSize: '0.7rem' }}>
                  <Box component="span" sx={{ fontSize: '0.66rem', color: COLORS.textSubtle, mr: 0.5 }}>BB</Box>
                  <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtN(bbTonsNum)}</Box>
                </Box>
                <Box component="span" sx={{ fontSize: '0.7rem' }}>
                  <Box component="span" sx={{ fontSize: '0.66rem', color: COLORS.textSubtle, mr: 0.5 }}>PP/JB</Box>
                  <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtN(ppjbTonsNum)}</Box>
                </Box>
              </Stack>
            </Stack>
            <Table size="small" sx={{ '& .MuiTableCell-root': { pl: 0.25, pr: 0 }, '& .MuiTableCell-root:first-of-type': { whiteSpace: 'nowrap' } }}>
              <TableBody>
                <HandlingLookupRow label="Intake" value={cost.intake} hint="VND/ton, weighted by issued tons across pallet / stack / processing. From Cost master." expired={costAlerts.intake} />
                <HandlingManualRow label="Transport to hopper" value={vcHopper} onChange={setNum(setVcHopper)} disabled={!editable} />
                <HandlingLookupRow label="Hopper discharge" value={cost.hopper} hint="VND/ton, BB vs PP·JB weighted by input bag mix. From Cost master." expired={costAlerts.hopper} />
                <HandlingLookupRow label="Packing" value={cost.packing} hint="VND/ton, BB vs PP·JB weighted by input bag mix (same tons as Hopper). From Cost master." expired={costAlerts.packing} />
                <HandlingManualRow label="Transport to warehouse" value={vcWarehouse} onChange={setNum(setVcWarehouse)} disabled={!editable} />
                <TableRow>
                  <TotalCell sx={{ py: 0.75, fontSize: COST_VALUE_FS }}>Total (VND)</TotalCell>
                  <TotalCell align="right" sx={{ py: 0.75, fontSize: COST_VALUE_FS }}>{fmtN(cost.handlingTotal, 2)}</TotalCell>
                </TableRow>
                <ResultRow label="Labor / Mt (= Total / FX)" value={`$${fmtN(cost.laborPerMt)}`} />
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Power → Power/Mt */}
        <Box sx={{ ...sectionCardSx, flex: '1 1 0', minWidth: 240 }}>
          <Box sx={{ ...sectionHeaderSx, px: 1.25 }}>
            <Typography sx={{ ...sectionTitleSx, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Power consumption → Power / Mt</Typography>
            <Chip size="small" label="kWh · VND" sx={neutralChipSx} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { pl: 0.25, pr: 0 }, '& .MuiTableCell-root:first-of-type': { whiteSpace: 'nowrap' } }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 0, py: 0.5, fontSize: COST_LABEL_FS, color: COLORS.textMuted }}>
                    Meter new (kWh)
                    <Tooltip title="Main meter, end-of-batch reading. Per PO.">
                      <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: COLORS.textSubtle }}>✎</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0, py: 0.5, width: 116 }}>
                    <CellInput value={meterNew} onChange={setNum(setMeterNew)} width={104} decimals={2} disabled={!editable} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, py: 0.5, fontSize: COST_LABEL_FS, color: COLORS.textMuted }}>
                    Meter old (kWh)
                    <Tooltip title="Main meter, start-of-batch reading. Consumption = new − old. Per PO.">
                      <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: COLORS.textSubtle }}>✎</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0, py: 0.5, width: 116 }}>
                    <CellInput value={meterOld} onChange={setNum(setMeterOld)} width={104} decimals={2} disabled={!editable} />
                  </TableCell>
                </TableRow>
                {/* Công tơ phụ (sub-meter) — điện KHÔNG thuộc mẻ, trừ ra; nhập theo PO. */}
                <TableRow>
                  <TableCell sx={{ border: 0, py: 0.5, fontSize: COST_LABEL_FS, color: COLORS.textMuted }}>
                    Sub-meter new (kWh)
                    <Tooltip title="Sub-meter for electricity not in this batch. New − old is subtracted. Per PO.">
                      <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: COLORS.textSubtle }}>✎</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0, py: 0.5, width: 116 }}>
                    <CellInput value={submeterNew} onChange={setNum(setSubmeterNew)} width={104} decimals={2} disabled={!editable} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, py: 0.5, fontSize: COST_LABEL_FS, color: COLORS.textMuted }}>
                    Sub-meter old (kWh)
                    <Tooltip title="Sub-meter for electricity not in this batch. New − old is subtracted. Per PO.">
                      <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: COLORS.textSubtle }}>✎</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0, py: 0.5, width: 116 }}>
                    <CellInput value={submeterOld} onChange={setNum(setSubmeterOld)} width={104} decimals={2} disabled={!editable} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, py: 0.5, fontSize: COST_LABEL_FS, color: COLORS.textMuted }}>
                    Extra deduct (kWh)
                    <Tooltip title="Extra kWh to deduct beyond sub-meter. kWh = (meter new − old) − (sub new − old) − extra. Per PO, default 0.">
                      <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: COLORS.textSubtle }}>✎</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0, py: 0.5, width: 116 }}>
                    <CellInput value={extraDeduct} onChange={setNum(setExtraDeduct)} width={104} decimals={2} disabled={!editable} />
                  </TableCell>
                </TableRow>
                {/* Tổng trừ (= (sub_new − sub_old) + extra) — read-only, tính lại live. */}
                <PowerRow label="Sub-meter deduct (kWh)" value={fmtN(toNum(submeterNew) - toNum(submeterOld) + toNum(extraDeduct))} />
                <PowerRow label="kWh consumed" value={fmtN(cost.kwh)} strong />
                <TableRow>
                  <TableCell sx={{ border: 0, py: 0.5, fontSize: COST_LABEL_FS, color: costAlerts.electricity ? COLORS.warn : COLORS.textMuted, fontWeight: costAlerts.electricity ? 700 : 400 }}>
                    Unit price (VND/kWh)
                    {costAlerts.electricity ? (
                      <Tooltip title="Electricity unit price is outside its valid period for this PO date — check Valid From / Valid To in Cost master">
                        <Box component="span" sx={{ ml: 0.5, fontSize: '0.72rem', color: COLORS.warn, fontWeight: 800 }}>⚠</Box>
                      </Tooltip>
                    ) : (
                      <Tooltip title="VND/kWh from Cost master. Electricity cost = kWh × price.">
                        <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: COLORS.primary }}>ⓘ</Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0, py: 0.5, width: 116 }}>
                    <Typography component="span" sx={{ fontWeight: costAlerts.electricity ? 900 : 700, fontSize: COST_VALUE_FS, color: costAlerts.electricity ? COLORS.warn : 'inherit' }}>{fmtN(cost.electricityUnit, 0)}</Typography>
                  </TableCell>
                </TableRow>
                <PowerRow label="Electricity cost (VND)" value={fmtN(cost.electricity)} />
                <PowerRow label="Electricity / Mt (VND)" value={fmtN(cost.costPerTon, 2)} />
                <ResultRow label="Power / Mt (= /Mt / FX)" value={`$${fmtN(cost.powerPerMt)}`} expired={costAlerts.electricity} />
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Combined */}
        <Box sx={{ ...sectionCardSx, flex: '1 1 0', minWidth: 240, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ ...sectionHeaderSx, px: 1.25 }}>
            <Typography sx={{ ...sectionTitleSx, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Var. proc. cost / Mt</Typography>
            <Chip size="small" label="USD / Mt" sx={neutralChipSx} />
          </Box>
          <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 0.75 }}>
            <CostResult label="Labor / Mt" value={`$${fmtN(cost.laborPerMt)}`} />
            <CostResult label="Power / Mt" value={`$${fmtN(cost.powerPerMt)}`} />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ borderTop: `1px solid ${COLORS.borderStrong}`, pt: 0.75 }}
            >
              <Typography sx={{ fontSize: COST_LABEL_FS, fontWeight: 700, color: COLORS.warn, whiteSpace: 'nowrap' }}>Var. proc. / Mt</Typography>
              <Typography sx={{ fontSize: COST_VALUE_FS, fontWeight: 900, color: COLORS.warn }}>${fmtN(cost.varProcPerMt)}</Typography>
            </Stack>
          </Box>
        </Box>
      </Stack>
      </Box>

      <Tooltip
        arrow
        placement="top-start"
        title={
          <Box sx={{ fontSize: '0.72rem', lineHeight: 1.6, display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 360 }}>
            <Box component="span">Value = LIFFE + G2 Diffs + Premium</Box>
            <Box component="span">Value In = Final In × Value · Value Out = Final Out × Value</Box>
            <Box component="span">Extracted / Mt = Total Value Out − Total Value In − Var. proc. cost / Total Outturn</Box>
            <Box component="span">Final = Edited value if entered, otherwise Default (from BomCode); Value In/Out and totals use Final.</Box>
            <Box component="span">Edit % In / % Out + manual costs (V/C transport, electricity unit price) — everything recomputes live (mirrors Excel BOM-{displayBomCode}).</Box>
            <Box component="span">Negatives shown in red parentheses; ⚑ marks off-BOM / edited vs the crop-year baseline.</Box>
          </Box>
        }
      >
        <Box
          component="span"
          sx={{
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.72rem',
            color: COLORS.textSubtle,
            cursor: 'help',
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>ⓘ</Box>
          Formula &amp; legend
        </Box>
      </Tooltip>

      <CostDetailsDialog open={costOpen} onClose={() => setCostOpen(false)} rows={costList?.items ?? []} loading={costFetching} poDateISO={poDateISO} />
    </Stack>
  )
})

export default BomTab

/* ---------- sub-components ---------------------------------------------- */

/** Popup bảng đơn giá chi phí (mirror sheet Cost) — group theo cost group,
 *  hiện display code / item / unit price / unit / method. Data từ bom-cost master. */
function CostDetailsDialog({
  open,
  onClose,
  rows,
  loading,
  poDateISO,
}: {
  open: boolean
  onClose: () => void
  rows: CostListRow[]
  loading: boolean
  /** Ngày PO (ISO) để xác định đơn giá quá hạn (ngoài valid_from/valid_to). */
  poDateISO?: string
}) {
  // Quá hạn = ngày PO ngoài [valid_from, valid_to] (giống rule costByCode) →
  // đơn giá KHÔNG áp dụng (calc ép 0). Chưa chọn PO → coi như còn hiệu lực.
  const dayOf = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '')
  const isRowExpired = (it: CostListRow): boolean => {
    const asOf = poDateISO || ''
    if (!asOf) return false
    const from = dayOf(it.valid_from)
    const to = dayOf(it.valid_to)
    if (from && asOf < from) return true
    if (to && asOf > to) return true
    return false
  }
  // 1 dòng / cost_item_code = đơn giá ĐANG DÙNG cho PO: ưu tiên row in-effect
  // (ngày PO trong [valid_from, valid_to]); nếu tất cả quá hạn → lấy row GẦN
  // NHẤT (valid_from mới nhất) để show với format quá hạn (đỏ + gạch). Mirror
  // rule `costByCode` của khối tính.
  const pickedRows = useMemo(() => {
    const byCode = new Map<string, CostListRow>()
    const sorted = [...rows].sort((a, b) => dayOf(a.valid_from).localeCompare(dayOf(b.valid_from)))
    for (const r of sorted) {
      const cur = byCode.get(r.cost_item_code)
      if (!cur) { byCode.set(r.cost_item_code, r); continue }
      const rEff = !isRowExpired(r)
      const curEff = !isRowExpired(cur)
      // r đứng sau (valid_from mới hơn). Set khi r in-effect, hoặc khi cur cũng
      // quá hạn (→ giữ row gần nhất). Cur in-effect + r quá hạn → giữ cur.
      if (rEff || !curEff) byCode.set(r.cost_item_code, r)
    }
    return [...byCode.values()]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, poDateISO])

  // Group theo cost group (giữ thứ tự sort_order), trong group theo item sort.
  const groups = useMemo(() => {
    const byGroup = new Map<string, { name: string; sort: number; items: CostListRow[] }>()
    for (const r of pickedRows) {
      const key = r.cost_group_code
      if (!byGroup.has(key)) {
        byGroup.set(key, {
          name: r.cost_group_name_en || r.cost_group_name_vi || key,
          sort: r.cost_group_sort_order ?? 0,
          items: [],
        })
      }
      byGroup.get(key)!.items.push(r)
    }
    const list = [...byGroup.values()].sort((a, b) => a.sort - b.sort)
    list.forEach((g) =>
      g.items.sort((a, b) => (a.cost_item_sort_order ?? 0) - (b.cost_item_sort_order ?? 0)),
    )
    return list
  }, [pickedRows])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6, fontSize: '0.95rem', fontWeight: 800, color: COLORS.text }}>
        Unit costs
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: COLORS.textMuted }}>
          Handling cost rates per Mt by bag type
        </Typography>
        {poDateISO ? (
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: COLORS.danger, mt: 0.25 }}>
            Rates outside the valid period for PO date {fmtDate(poDateISO)} are struck through and counted as 0.
          </Typography>
        ) : null}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8, color: COLORS.textMuted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : groups.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center', fontSize: '0.8rem', color: COLORS.textSubtle }}>
            No cost items available.
          </Typography>
        ) : (
          <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.74rem', py: 0.6, px: 1.25 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, width: 56 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Item</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, width: 100 }}>Unit price</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800, width: 56 }}>Unit</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800, width: 150 }}>Validity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <Fragment key={g.name}>
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{ fontWeight: 800, color: COLORS.primary, bgcolor: COLORS.bgSubtle, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem !important' }}
                    >
                      {g.name}
                    </TableCell>
                  </TableRow>
                  {g.items.map((it) => {
                    const exp = isRowExpired(it)
                    return (
                    <TableRow key={it.cost_item_id} hover>
                      <TableCell sx={{ color: exp ? COLORS.danger : COLORS.textMuted }}>{it.display_code || '–'}</TableCell>
                      <TableCell sx={exp ? { color: COLORS.danger, fontWeight: 700 } : undefined}>
                        {it.cost_item_name_en || it.cost_item_name_vi}{exp ? ' ⚠' : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: exp ? COLORS.danger : undefined, textDecoration: exp ? 'line-through' : undefined }}>
                        {it.unit_price == null ? '–' : fmtN(Number(it.unit_price))}
                      </TableCell>
                      <TableCell align="center" sx={{ color: exp ? COLORS.danger : COLORS.textMuted }}>
                        {it.currency && it.price_unit ? `${it.currency}/${it.price_unit}` : it.price_unit || it.currency || '–'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.66rem !important', whiteSpace: 'nowrap', color: exp ? COLORS.danger : COLORS.textMuted, fontWeight: exp ? 700 : 400 }}>
                        {it.valid_from ? fmtDate(dayOf(it.valid_from)) : '—'} – {it.valid_to ? fmtDate(dayOf(it.valid_to)) : '—'}
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Handling step có đơn giá link từ cost master (read-only, ⓘ).
 *  `expired` = ngày PO ngoài [valid_from, valid_to] của đơn giá → tô đậm + ⚠. */
function HandlingLookupRow({ label, value, hint, expired }: { label: string; value: number; hint: string; expired?: boolean }) {
  return (
    <TableRow>
      <TableCell sx={{ border: 0, py: 0.5, fontSize: COST_LABEL_FS, color: expired ? COLORS.warn : COLORS.textMuted, fontWeight: expired ? 700 : 400 }}>
        {label}
        {expired ? (
          <Tooltip title="Unit price is outside its valid period for this PO date — check Valid From / Valid To in Cost master">
            <Box component="span" sx={{ ml: 0.5, fontSize: '0.72rem', color: COLORS.warn, fontWeight: 800 }}>⚠</Box>
          </Tooltip>
        ) : (
          <Tooltip title={hint}>
            <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: COLORS.primary }}>ⓘ</Box>
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.5, width: 116 }}>
        <Typography component="span" sx={{ fontWeight: expired ? 900 : 700, fontSize: COST_VALUE_FS, color: expired ? COLORS.warn : 'inherit' }}>{fmtN(value, 2)}</Typography>
      </TableCell>
    </TableRow>
  )
}

/** Handling step V/C manual (chưa có nguồn BE) — editable. */
function HandlingManualRow({ label, value, onChange, disabled }: { label: string; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <TableRow>
      <TableCell sx={{ border: 0, py: 0.5, fontSize: COST_LABEL_FS, color: COLORS.textMuted }}>
        {label}
        <Tooltip title="VND/ton, manual entry (no master). Saved per PO. Default 0.">
          <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', color: COLORS.textSubtle }}>✎</Box>
        </Tooltip>
      </TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.5, width: 116 }}>
        <CellInput value={value} onChange={onChange} width={104} decimals={2} disabled={disabled} />
      </TableCell>
    </TableRow>
  )
}

/** Style label field (Year, Month, Input quality…) — UPPERCASE nhỏ để phân biệt
 *  rõ với value (value thường, to, đậm); vẫn màu đậm, không nhạt. */
function DiffMark({ diff, baseOut }: { diff: DiffStatus; baseOut: number | null }) {
  if (diff === 'unchanged') return null
  const map = {
    added: { ch: '＋', title: 'Off-BOM quality (not in crop-year BOM)', color: DIFF_COLOR.added },
    modified: { ch: '●', title: `Edited vs baseline (Outturn base ${baseOut ?? 0})`, color: DIFF_COLOR.modified },
    removed: { ch: '－', title: 'Baseline grade zeroed on this PO', color: DIFF_COLOR.removed },
  }[diff]
  return (
    <Tooltip title={map.title}>
      <Box component="span" sx={{ fontWeight: 700, fontSize: '0.85rem', color: map.color, cursor: 'help' }}>{map.ch}</Box>
    </Tooltip>
  )
}

/** Header cột 2 dòng: `main` dòng 1, `sub` (qualifier trong ngoặc) dòng 2 —
 *  giúp "% In (Edit)" / "(Default)" / "(Finalize)" không tràn ngang cột hẹp. */
function TwoLineHead({ main, sub }: { main: string; sub?: string }) {
  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.15 }}>
      <Box component="span">{main}</Box>
      {sub ? (
        <Box component="span" sx={{ fontSize: '0.92em', fontWeight: 600, color: COLORS.textMuted }}>{sub}</Box>
      ) : null}
    </Box>
  )
}

function ActiveBadge({ active }: { active: 'Y' | 'N' }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        minWidth: 22,
        px: 0.75,
        py: 0.1,
        borderRadius: 999,
        fontSize: '0.72rem',
        fontWeight: 700,
        bgcolor: active === 'Y' ? COLORS.successSoft : COLORS.bgHeader,
        color: active === 'Y' ? COLORS.success : COLORS.textSubtle,
      }}
    >
      {active}
    </Box>
  )
}

/** Ô editable số: hiển thị Standard (`#,##0.{decimals}`) khi blur, raw khi focus
 *  (format-on-blur, căn phải). `decimals` mặc định 3 (Mt). */
function CellInput({ value, onChange, width = 92, decimals = 3, disabled }: { value: string; onChange: (v: string) => void; width?: number; decimals?: number; disabled?: boolean }) {
  const [focused, setFocused] = useState(false)
  const display = focused || value === '' ? value : fmtN(toNum(value), decimals)
  return (
    <TextField
      variant="outlined"
      size="small"
      placeholder="0"
      disabled={disabled}
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', padding: '4px 8px', fontSize: '0.7rem' } }}
      sx={{
        width,
        '& .MuiOutlinedInput-root': {
          bgcolor: COLORS.primarySoft,
          '& fieldset': { borderColor: COLORS.primary, borderWidth: 1.5 },
          '&:hover fieldset': { borderColor: COLORS.primary, borderWidth: 1.5 },
          '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: 2 },
          '&.Mui-focused': { bgcolor: '#fff' },
        },
        '& input': { fontWeight: 700, color: COLORS.text },
      }}
    />
  )
}

/** Ô editable PHẦN TRĂM (Input F / Outturn H — Excel format `0.0%`).
 *  Lưu trữ vẫn là PHÂN SỐ (vd `0.365`) để math `Input×Value` không đổi; UI edit theo
 *  đơn vị % (user gõ `36.5` ⇒ lưu `0.365`). Blur → hiển thị `36.5%`; focus → số % thô. */
function PercentCellInput({ value, onChange, width = 92, disabled }: { value: string; onChange: (v: string) => void; width?: number; disabled?: boolean }) {
  // `editing` = chuỗi % đang gõ (null khi không focus → hiển thị format `xx.x%`).
  const [editing, setEditing] = useState<string | null>(null)
  const frac = toNum(value)
  const display =
    editing != null
      ? editing
      : value === ''
        ? ''
        : fmtPct(frac * 100, 1)
  return (
    <TextField
      variant="outlined"
      size="small"
      placeholder="0"
      disabled={disabled}
      value={display}
      onFocus={() => setEditing(value === '' ? '' : String(Math.round(frac * 100 * 1e6) / 1e6))}
      onBlur={() => setEditing(null)}
      onChange={(e) => {
        const raw = e.target.value
        if (!numericGuard(raw)) return // % units: digits + 1 dấu thập phân, không âm
        setEditing(raw)
        // Quy đổi về phân số khi lưu (rỗng giữ rỗng).
        onChange(raw === '' ? '' : String(toNum(raw) / 100))
      }}
      inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', padding: '4px 8px', fontSize: '0.7rem' } }}
      sx={{
        width,
        '& .MuiOutlinedInput-root': {
          bgcolor: COLORS.primarySoft,
          '& fieldset': { borderColor: COLORS.primary, borderWidth: 1.5 },
          '&:hover fieldset': { borderColor: COLORS.primary, borderWidth: 1.5 },
          '&.Mui-focused fieldset': { borderColor: COLORS.primary, borderWidth: 2 },
          '&.Mui-focused': { bgcolor: '#fff' },
        },
        '& input': { fontWeight: 700, color: COLORS.text },
      }}
    />
  )
}

/** Nút Save delta cho block (Detail/Cost) — compact, cạnh BlockTitle. */
function SaveDeltaButton({
  label,
  onClick,
  disabled,
  busy,
  hint,
}: {
  label: string
  onClick: () => void
  disabled: boolean
  busy: boolean
  hint?: string
}) {
  return (
    <Tooltip title={hint || ''}>
      <span>
        <Button
          variant="contained"
          size="small"
          color="success"
          disabled={disabled}
          onClick={onClick}
          startIcon={busy ? <CircularProgress size={13} color="inherit" /> : <SaveOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
          sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 700, py: 0.2, px: 1, minHeight: 0 }}
        >
          {busy ? 'Saving…' : label}
        </Button>
      </span>
    </Tooltip>
  )
}

// Font đồng nhất cho 3 cost panels: label 0.8rem, value 0.85rem.
// Cost Breakdown — cùng 1 cỡ label/value + format cho cả 3 panel (Handling /
// Power / Var. proc.); label nhỏ + nowrap để dòng dài không xuống 2 dòng.
const COST_LABEL_FS = '0.72rem'
const COST_VALUE_FS = '0.7rem'

function CostResult({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 0.5 }}>
      <Typography sx={{ fontSize: COST_LABEL_FS, color: COLORS.textMuted, whiteSpace: 'nowrap' }}>{label}</Typography>
      <Typography sx={{ fontSize: COST_VALUE_FS, fontWeight: 800, color: COLORS.text }}>{value}</Typography>
    </Stack>
  )
}

function PowerRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <TableRow>
      <TableCell sx={{ border: 0, py: 0.5, fontSize: COST_LABEL_FS, color: COLORS.textMuted }}>{label}</TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.5, fontSize: COST_VALUE_FS, fontWeight: strong ? 800 : 700, color: strong ? COLORS.primary : COLORS.text }}>
        {value}
      </TableCell>
    </TableRow>
  )
}

/** Dòng kết quả (Labor/Mt, Power/Mt) trong bảng cost — value căn cùng cột với
 *  các dòng value phía trên, highlight warn + border top. */
function ResultRow({ label, value, expired }: { label: string; value: string; expired?: boolean }) {
  // expired = đơn giá nguồn quá hạn (đã ép 0) → tô ĐỎ thay vì cam cảnh báo.
  const sx = { border: 0, py: 0.75, fontSize: COST_VALUE_FS, fontWeight: 800, color: expired ? COLORS.danger : COLORS.warn, borderTop: `1px solid ${COLORS.borderStrong}` }
  return (
    <TableRow>
      <TableCell sx={sx}>{label}</TableCell>
      <TableCell align="right" sx={sx}>{value}</TableCell>
    </TableRow>
  )
}

function TotalCell({ children, colSpan, align, sx }: { children?: React.ReactNode; colSpan?: number; align?: 'right'; sx?: object }) {
  return (
    <TableCell
      colSpan={colSpan}
      align={align}
      sx={{ fontWeight: 700, bgcolor: COLORS.bgSubtle, borderTop: `2px solid ${COLORS.borderStrong}`, ...sx }}
    >
      {children}
    </TableCell>
  )
}
